import React, { useRef, useState, useEffect } from "react";
import { apiFetch } from "../../../utils/apiFetch";
import { useTranslation } from "react-i18next";
import ToolbarTooltipButton from "../../../components/common/ToolbarTooltipButton";
import Button from "../../../components/common/Button";
import HeatmapViewer from "./HeatmapViewer";
import LogoEditor from "./LogoEditor";
import HeatmapOverlay from "./HeatmapOverlay";
import { toFetchableUrl } from "../../../utils/imageFetch";
import LogoPickerModal from "./LogoPickerModal";
import useLogos from "../../../utils/useLogos";
import { copyImageToClipboard } from "../../../utils/clipboard";
import regenerate from "../../../assets/tips/regenerate.svg";
import copyIcon from "../../../assets/posts/copy.svg";
import deleteIcon from "../../../assets/tips/delete.svg";
import nano from "../../../assets/tips/nano.svg";
import logoIcon from "../../../assets/tips/uploadLogo.svg";
import eye from "../../../assets/posts/eye.svg";
import heatmapIcon from "../../../assets/tips/heatmap.svg";

export default function ImageSection({
  selectedPostId,
  imageLocked,
  isInsightsLoading,
  mobileViewStep,
  generatingImage,
  imageError,
  generatedImages = [],
  selectedImageIndex = 0,
  onSelectImage,
  generatedImagesCount = 0,
  maxGeneratedImages = 5,
  showGeneratedImageUI,
  imageUrl,
  imageFile = null,
  imageSectionRef,
  onRegenerateImage,
  onDeleteImage,
  handleUploadedImagesReady,
  imgCopied,
  setImgCopied,
  isEditMode = false,
  onLogoApplied,
  onPreviewClick,
  onDownload,
  userEmail = "",
  onOpenAccount,
  preLogoImage = null,
}) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);
  const [generatedOriginalLoaded, setGeneratedOriginalLoaded] = useState(false);
  const [generatedHeatmapLoaded, setGeneratedHeatmapLoaded] = useState(false);
  const [heatmapUrl, setHeatmapUrl] = useState(null);
  const [hotzones, setHotzones] = useState([]);
  const [originalImageSize, setOriginalImageSize] = useState({ width: 1, height: 1 });
  const [isHeatmapLoading, setIsHeatmapLoading] = useState(false);
  const [heatmapError, setHeatmapError] = useState("");
  const [showLogoPanel, setShowLogoPanel] = useState(false);
  const [logoInitialFile, setLogoInitialFile] = useState(null);
  const showHeatmapViewer = !!imageUrl;
  const activeGeneratedImg = generatedImages[selectedImageIndex] || null;
  const { logos: savedLogos } = useLogos(userEmail);
  const [showLogoPicker, setShowLogoPicker] = useState(false);
  const [logoPickError, setLogoPickError] = useState("");
  const [showLogoTools, setShowLogoTools] = useState(false);

  const fetchHeatmap = async (file, autoEnable = false) => {
    if (!file) return;
    setIsHeatmapLoading(true);
    setHeatmapError("");
    try {
      const formData = new FormData();
      formData.append("image", file);
      const response = await apiFetch(process.env.REACT_APP_HEATMAP_API_URL, {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        console.error("❌ Heatmap error:", response.status, body?.error);
        // 401 is handled by apiFetch (session-expired modal), so no message here.
        if (response.status === 413 || body?.error === "image_too_large") {
          setHeatmapError(t("insights.heatmapTooLarge"));
        } else if (response.status === 400 || body?.error === "invalid_image") {
          setHeatmapError(t("insights.heatmapInvalid"));
        } else if (response.status !== 401) {
          setHeatmapError(t("insights.heatmapFailed"));
        }
        return;
      }
      const data = await response.json();
      setHeatmapUrl(data.blob_url);
      setHotzones(data.coordinates || []);
      if (autoEnable) setEnabled(true);
    } catch (err) {
      console.error("❌ Heatmap failed:", err);
      setHeatmapError(t("insights.heatmapFailed"));
    } finally {
      setIsHeatmapLoading(false);
    }
  };

  const fetchHeatmapFromUrl = async (url, autoEnable = false) => {
    if (!url) return;
    try {
      const response = await fetch(toFetchableUrl(url));
      if (!response.ok) throw new Error(`Image fetch failed: ${response.status}`);
      const blob = await response.blob();
      const file = new File([blob], "image.png", { type: blob.type || "image/png" });
      return fetchHeatmap(file, autoEnable);
    } catch (err) {
      console.error("❌ Failed to load image for heatmap:", err);
    }
  };

  useEffect(() => {
    if (imageFile) {
      setHeatmapUrl(null);
      setHotzones([]);
      fetchHeatmap(imageFile, true);
    }
  }, [imageFile]);

  useEffect(() => {
    if (!activeGeneratedImg) return;
    setHeatmapUrl(null);
    setHotzones([]);
    fetchHeatmapFromUrl(activeGeneratedImg, false);
  }, [activeGeneratedImg]);

  useEffect(() => {
    if (!imageUrl && !showGeneratedImageUI) {
      setHeatmapUrl(null);
      setHotzones([]);
      setEnabled(false);
    }
  }, [imageUrl, showGeneratedImageUI]);

  // One toggle for uploaded and generated images. Turning it ON loads the
  // heatmap if none is loaded (and none is already on its way).
  const handleHeatmapToggle = () => {
    const next = !enabled;
    setEnabled(next);
    if (next && !heatmapUrl && !isHeatmapLoading) {
      fetchHeatmapFromUrl(showHeatmapViewer ? imageUrl : activeGeneratedImg);
    }
  };

  // Saved logos are blob URLs; LogoEditor needs a File, so fetch and wrap it.
  const handlePickSavedLogo = async (src) => {
    setLogoPickError("");
    try {
      const res = await fetch(src);
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      setLogoInitialFile(new File([blob], "logo.png", { type: blob.type || "image/png" }));
      setShowLogoPicker(false);
      setShowLogoPanel(true);
    } catch (err) {
      console.error("Logo fetch failed (CORS on the blob container?):", err);
      setLogoPickError(t("logoEditor.loadFailed"));
    }
  };

  // With the editor open the logo button toggles its tools; otherwise it
  // opens the picker.
  const handleLogoButton = () =>
    showLogoPanel ? setShowLogoTools((v) => !v) : setShowLogoPicker(true);

  // The copy starts immediately: Safari drops the tap permission if anything is awaited first.
  const copyImage = async (src) => {
    if (!src) return;
    try {
      await copyImageToClipboard(src);
      setImgCopied?.(true);
      setTimeout(() => setImgCopied?.(false), 2000);
    } catch (err) {
      console.error("Image copy failed:", err);
    }
  };

  // The editor always works from the pre-logo image when one exists, so a
  // second Apply replaces the logo rather than stacking on a flattened one.
  const logoBgFile = preLogoImage?.file ?? imageFile;
  const logoBgUrlUploaded = preLogoImage?.url ?? imageUrl;
  const logoBgUrlGenerated = preLogoImage?.url ?? activeGeneratedImg;

  return (
    <div id="section-image-choice" className="flex-1 flex flex-col">

      {imageError && (
        <div className="mt-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg px-4 py-3">
          {imageError}
        </div>
      )}

      {heatmapError && (
        <div className="mt-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg px-4 py-3">
          {heatmapError}
        </div>
      )}

      {/* ===== UPLOADED IMAGE HEATMAP VIEWER ===== */}
      {mobileViewStep !== 3 && showHeatmapViewer && (
        <div className="relative" ref={imageSectionRef}>
          <div className="flex flex-wrap items-center justify-start sm:justify-end gap-x-2 gap-y-0 p-2 bg-black-20 rounded-tr-lg rounded-tl-lg w-full relative z-20 border border-b-0 border-gray-200">
            <ToolbarTooltipButton
              label={t("logoEditor.uploadLogo")}
              showLabel
              highlight
              badge="New"
              onClick={handleLogoButton}
            >
              <img src={logoIcon} alt="logo" className="w-4 h-4" />
            </ToolbarTooltipButton>
            <ToolbarTooltipButton
              label={t("insights.heatmapToggle")}
              showLabel
              active={enabled}
              disabled={showLogoPanel}
              onClick={handleHeatmapToggle}
            >
              <img src={heatmapIcon} alt="" className="w-4 h-4" />
            </ToolbarTooltipButton>
            <ToolbarTooltipButton label={t("buttons.copy")} onClick={() => copyImage(imageUrl)}>
              <img src={copyIcon} alt="copy" className="w-4 h-4" />
            </ToolbarTooltipButton>
            <ToolbarTooltipButton
              label={t("buttons.remove")}
              disabled={showLogoPanel}
              onClick={() => {
                setHeatmapUrl(null);
                setHotzones([]);
                setHeatmapError("");
                setEnabled(false);
                onDeleteImage?.();
              }}
            >
              <img src={deleteIcon} alt="" className="w-4 h-4" />
            </ToolbarTooltipButton>
          </div>
          {showLogoPanel ? (
            <div className="mb-4">
              <LogoEditor
                backgroundFile={logoBgFile}
                backgroundUrl={logoBgUrlUploaded}
                initialLogoFile={logoInitialFile}
                showTools={showLogoTools}
                onToolsChange={setShowLogoTools}
                onChangeLogo={() => setShowLogoPicker(true)}
                onApply={(blob) => {
                  onLogoApplied?.(blob, true);
                  setShowLogoPanel(false);
                  setShowLogoTools(false);
                  setLogoInitialFile(null);
                }}
                onClose={() => {
                  setShowLogoPanel(false);
                  setShowLogoTools(false);
                  setLogoInitialFile(null);
                }}
              />
            </div>
          ) : (
            <HeatmapViewer
              originalImage={imageUrl}
              heatmapImage={heatmapUrl}
              hotzones={hotzones}
              isHeatmapLoading={isHeatmapLoading}
              onOriginalImageLoad={(w, h) => setOriginalImageSize({ width: w, height: h })}
              originalImageSize={originalImageSize}
              suggestions={[
                { title: t("imageRef.color"), text: <span className="text-xl bg-Brand-lemon-100 text-black-800 px-2 py-1 rounded w-fit">{t("updates.newFeature")}</span>, recommended: true },
                { title: t("imageRef.element"), text: <span className="text-xl bg-Brand-lemon-100 text-black-800 px-2 py-1 rounded w-fit">{t("updates.newFeature")}</span>, recommended: true },
                { title: t("imageRef.context"), text: <span className="text-xl bg-Brand-lemon-100 text-black-800 px-2 py-1 rounded w-fit">{t("updates.newFeature")}</span>, recommended: false },
                { title: t("imageRef.context"), text: <span className="text-xl bg-Brand-lemon-100 text-black-800 px-2 py-1 rounded w-fit">{t("updates.newFeature")}</span>, recommended: false },
              ]}
              onImagesReady={handleUploadedImagesReady}
              onDeleteImage={onDeleteImage}
              imageUrl={imageUrl}
              imageLocked={imageLocked}
              showToolbar={false}
              enabled={enabled}
              onLogoClick={() => setShowLogoPicker(true)}
            />
          )}
        </div>
      )}

      {/* ===== GENERATED IMAGE DISPLAY ===== */}
      {mobileViewStep !== 3 && showGeneratedImageUI && (
        <div id="section-image-generated" ref={imageSectionRef} className="mb-4 relative">
          <p className="text-lg sm:text-xl font-bold text-black-800 mb-3 flex gap-2">
            <img src={nano} alt="nano" className="w-4 h-6" />
            {t("insights.generateImgTitle")}
          </p>

          {imageLocked && (
            <div className="flex flex-wrap items-center justify-start sm:justify-end gap-x-2 gap-y-0 p-2 bg-black-20 rounded-tr-lg rounded-tl-lg w-full relative z-20 border border-b-0 border-gray-200">
              <ToolbarTooltipButton
                label={t("logoEditor.uploadLogo")}
                showLabel
                highlight
                badge="New"
                onClick={handleLogoButton}
              >
                <img src={logoIcon} alt="logo" className="w-4 h-4" />
              </ToolbarTooltipButton>
              <ToolbarTooltipButton
                label={t("buttons.regenerate")}
                tooltip={t("buttons.regenerateImage")}
                showLabel
                onClick={onRegenerateImage}
                disabled={generatedImagesCount >= maxGeneratedImages}
              >
                <img src={regenerate} alt="" className="w-4 h-4" />
              </ToolbarTooltipButton>
              <ToolbarTooltipButton
                label={t("insights.heatmapToggle")}
                showLabel
                active={enabled}
                disabled={showLogoPanel}
                onClick={handleHeatmapToggle}
              >
                <img src={heatmapIcon} alt="" className="w-4 h-4" />
              </ToolbarTooltipButton>
              <ToolbarTooltipButton label={t("insights.preview")} onClick={onPreviewClick}>
                <img src={eye} alt="preview" className="w-4 h-4" />
              </ToolbarTooltipButton>
              <ToolbarTooltipButton
                label={t("buttons.copy")}
                onClick={() => copyImage(activeGeneratedImg)}
              >
                <img src={copyIcon} alt="copy" className="w-4 h-4" />
              </ToolbarTooltipButton>
            </div>
          )}

          {/* ===== IMAGE COMPARISON ===== */}
          {showLogoPanel ? (
            <div className="mb-4">
              <LogoEditor
                backgroundUrl={logoBgUrlGenerated}
                initialLogoFile={logoInitialFile}
                showTools={showLogoTools}
                onToolsChange={setShowLogoTools}
                onChangeLogo={() => setShowLogoPicker(true)}
                onApply={(blob) => {
                  onLogoApplied?.(blob);
                  setShowLogoPanel(false);
                  setShowLogoTools(false);
                  setLogoInitialFile(null);
                }}
                onClose={() => {
                  setShowLogoPanel(false);
                  setShowLogoTools(false);
                  setLogoInitialFile(null);
                }}
              />
            </div>
          ) : (
            <div
              ref={containerRef}
              className={`relative w-full select-none ${
                imageLocked ? "rounded-bl-lg rounded-br-lg" : "rounded-lg"
              }`}
              style={{ aspectRatio }}
            >
              {/* Inner layer — overflow-hidden for image/heatmap clipping only */}
              <div className={`absolute inset-0 overflow-hidden bg-gray-100 ${
                imageLocked ? "rounded-bl-lg rounded-br-lg" : "rounded-lg"
              }`}>
                {activeGeneratedImg ? (
                  <>
                    <img
                      src={activeGeneratedImg}
                      alt="Generated"
                      className="absolute inset-0 w-full h-full object-contain"
                      onLoad={(e) => {
                        setGeneratedOriginalLoaded(true);
                        const img = e.target;
                        setAspectRatio(img.naturalWidth / img.naturalHeight);
                        setOriginalImageSize({ width: img.naturalWidth, height: img.naturalHeight });
                      }}
                    />

                    {heatmapUrl && (
                      <div
                        className={`absolute inset-0 overflow-hidden transition-opacity duration-300 ${
                          enabled ? "opacity-100" : "opacity-0 pointer-events-none"
                        }`}
                      >
                        <img
                          src={heatmapUrl}
                          alt="Heatmap"
                          className="w-full h-full block"
                          onLoad={() => setGeneratedHeatmapLoaded(true)}
                        />
                      </div>
                    )}

                    {isHeatmapLoading && enabled && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-20">
                        <div className="w-8 h-8 border-4 border-primary-400 border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
                    {t("insights.noImageYet")}
                  </div>
                )}
              </div>
              
              {/* Attention dots + colour scale */}
              <HeatmapOverlay hotzones={hotzones} enabled={enabled} imageSize={originalImageSize} />
            </div>
          )}
          {generatedImages.length > 0 && (
            <>
              <div className="flex items-center justify-between mt-4 mb-2">
                <h3 className="font-bold text-lg text-black-800">
                  {t("insights.imagesLimit", { count: maxGeneratedImages })}
                </h3>
                <span className="text-xs font-bold text-black-400 ml-3 flex-shrink-0">
                  {generatedImagesCount}/{maxGeneratedImages}
                </span>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {generatedImages.map((imgUrl, idx) => (
                  <button
                    key={idx}
                    onClick={() => onSelectImage?.(idx)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-3 transition-all ${
                      idx === selectedImageIndex ? "border-primary-400" : "border-gray-200 hover:border-primary-300"
                    }`}
                  >
                    <img src={imgUrl} alt={`Generated ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <LogoPickerModal
        open={showLogoPicker}
        onClose={() => setShowLogoPicker(false)}
        logos={savedLogos}
        onSelect={handlePickSavedLogo}
        onOpenAccount={onOpenAccount}
        error={logoPickError}
      />
    </div>
  );
}